import { useState, useCallback, useMemo } from 'react';

/**
 * 循環訂單商品選擇 cart hook
 * cart 結構：{ [productID]: quantity }
 *
 * @param {Array} products - 可選購商品清單（由 API 取得），用於計算 cartSummary 與 totalPrice
 */
const useCyclePurchaseCart = (products = []) => {
    const [cart, setCart] = useState({});

    // 功能：購物車總件數（各商品數量加總，不依賴 products）
    const totalItems = useMemo(() => Object.values(cart).reduce((sum, qty) => sum + qty, 0), [cart]);
    const hasCartItems = totalItems > 0;

    // 功能：增加指定商品數量（不限上限）
    const addItem = useCallback((productId) => {
        setCart((prev) => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));
    }, []);

    // 功能：減少指定商品數量，降至 0 時移除該 key
    const removeItem = useCallback((productId) => {
        setCart((prev) => {
            const nextQty = (prev[productId] || 0) - 1;
            if (nextQty <= 0) {
                const next = { ...prev };
                delete next[productId];
                return next;
            }
            return { ...prev, [productId]: nextQty };
        });
    }, []);

    // 功能：以外部 cart map 重設整個購物車（用於從訂單原始數量初始化）
    const resetCart = useCallback((newCart = {}) => setCart({ ...newCart }), []);

    // 功能：清空購物車
    const clearCart = useCallback(() => setCart({}), []);

    // 功能：將 cart 轉為明細陣列，依 products 原始順序，過濾數量為 0 的項目
    const cartSummary = useMemo(() => {
        return products
            .filter((p) => cart[p.productID] > 0)
            .map((p) => ({
                productID: p.productID,
                variantID: p.variantID,
                productName: p.productName,
                price: p.price ?? 0,
                quantity: cart[p.productID],
            }));
    }, [products, cart]);

    const totalPrice = useMemo(
        () => cartSummary.reduce((sum, item) => sum + item.price * item.quantity, 0),
        [cartSummary],
    );

    return {
        cart,
        totalItems,
        totalPrice,
        hasCartItems,
        cartSummary,
        addItem,
        removeItem,
        resetCart,
        clearCart,
    };
};

export default useCyclePurchaseCart;
